import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Check, Trash2, ListChecks, AlertTriangle, ShieldCheck, Upload, Link2, FileText, History } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Modal, Input, Select } from "./Projects";
import { fmtDate, STATUS_COLORS, PRIORITY_LABELS } from "../lib/gameData";
import { celebrate } from "../components/CelebrationOverlay";
import { toast } from "sonner";

const FILTERS = ["Toutes", "À faire", "En cours", "Terminée"];

export default function Tasks() {
  const { refresh } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("Toutes");
  const [open, setOpen] = useState(false);
  const [proofTask, setProofTask] = useState(null);
  const [openHist, setOpenHist] = useState(null);

  const load = useCallback(async () => {
    const [t, c, p] = await Promise.all([api.get("/tasks"), api.get("/config"), api.get("/projects")]);
    setTasks(t.data); setConfig(c.data); setProjects(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (task) => {
    if (task.proof?.required) { setProofTask(task); return; }
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { status: "Terminée" });
      toast.success(`Tâche terminée · +${data.xp_awarded} XP`);
      if (data.leveled_up) celebrate("level");
      (data.new_badges || []).forEach(() => celebrate("badge"));
      await load(); await refresh();
    } catch (e) {
      if (e.response?.status === 409) setProofTask(task);
      else toast.error("Impossible de terminer la tâche.");
    }
  };

  const setStatus = async (task, status) => {
    const { data } = await api.put(`/tasks/${task.id}`, { status });
    if (data.xp_awarded) { toast.success(`+${data.xp_awarded} XP`); await refresh(); }
    load();
  };

  const del = async (id) => { await api.delete(`/tasks/${id}`); toast.success("Tâche supprimée"); load(); };

  if (!tasks) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const filtered = filter === "Toutes" ? tasks : tasks.filter((t) => t.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Tâches</h1>
          <p className="text-zinc-500 text-sm mt-1">Chaque action te rapproche de ton objectif.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-task-btn"
          className="inline-flex items-center gap-2 px-4 h-11 rounded-xl gold-gradient text-black font-semibold hover:opacity-90">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nouvelle tâche</span>
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${filter === f ? "gold-gradient text-black border-transparent" : "border-zinc-800 text-zinc-400 hover:border-amber-500/50"}`}>
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-12 text-center" data-testid="tasks-empty">
          <ListChecks className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Aucune tâche ici.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tasks-list">
          {filtered.map((t) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover" data-testid={`task-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${t.status === "Terminée" ? "line-through text-zinc-500" : ""}`}>{t.title}</p>
                  {t.description && <p className="text-sm text-zinc-500 mt-0.5 line-clamp-1">{t.description}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status]}`}>{t.status}</span>
                    {t.overdue && <span className="px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> En retard</span>}
                    {t.proof?.required && t.status !== "Terminée" && <span className="px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 flex items-center gap-1" data-testid={`proof-badge-${t.id}`}><ShieldCheck className="w-3 h-3" /> Preuve requise</span>}
                    {["Haute", "Moyenne", "Faible"].includes(t.priority) && (
                      <span className={`px-2 py-0.5 rounded-full border ${t.priority === "Haute" ? "text-red-400 border-red-500/40" : t.priority === "Moyenne" ? "text-amber-400 border-amber-500/40" : "text-emerald-400 border-emerald-500/40"}`}>{t.priority}</span>
                    )}
                    {t.project_title && <span className="text-amber-500/80">{t.project_title}</span>}
                    <span className="text-zinc-500">{fmtDate(t.deadline)}</span>
                    <span className="font-mono text-amber-400">+{t.xp} XP</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.status !== "Terminée" && (
                    <>
                      {t.status === "À faire" && (
                        <button onClick={() => setStatus(t, "En cours")} data-testid={`start-task-${t.id}`}
                          className="px-3 h-9 rounded-lg text-xs font-medium border border-zinc-700 text-zinc-300 hover:border-amber-500">Démarrer</button>
                      )}
                      <button onClick={() => complete(t)} data-testid={`complete-task-${t.id}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-medium hover:bg-emerald-500/25">
                        <Check className="w-4 h-4" /> Terminer
                      </button>
                    </>
                  )}
                  <button onClick={() => del(t.id)} data-testid={`delete-task-${t.id}`}
                    className="w-9 h-9 rounded-lg border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/30 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {t.proof?.required && (
                <ProofHistory taskId={t.id} open={openHist === t.id} onToggle={() => setOpenHist(openHist === t.id ? null : t.id)} />
              )}
            </motion.div>
          ))}
        </div>
      )}

      {open && <NewTaskModal config={config} projects={projects} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {proofTask && <ProofModal task={proofTask} onClose={() => setProofTask(null)} onDone={() => { setProofTask(null); load(); }} />}
    </div>
  );
}

const PROOF_TYPE_META = {
  TEXT: { label: "Texte", icon: FileText }, LINK: { label: "Lien", icon: Link2 },
  IMAGE: { label: "Image", icon: Upload }, AUDIO: { label: "Audio", icon: Upload }, VIDEO: { label: "Vidéo", icon: Upload },
};

function ProofModal({ task, onClose, onDone }) {
  const { refresh } = useAuth();
  const req = task.proof || {};
  const types = (req.allowed_types || ["TEXT"]).filter((t) => t !== "NONE" && t !== "MULTIPLE");
  const [type, setType] = useState(types[0] || "TEXT");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [duration, setDuration] = useState(null);
  const [loading, setLoading] = useState(false);
  const isFile = ["IMAGE", "AUDIO", "VIDEO"].includes(type);
  const accept = type === "IMAGE" ? "image/*" : type === "AUDIO" ? "audio/*" : type === "VIDEO" ? "video/*" : undefined;

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onFile = (f) => {
    setDuration(null);
    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setFile(f || null);
    if (!f) return;
    const objUrl = URL.createObjectURL(f);
    setPreview(objUrl);
    if (type === "AUDIO" || type === "VIDEO") {
      const el = document.createElement(type === "AUDIO" ? "audio" : "video");
      el.preload = "metadata";
      el.onloadedmetadata = () => setDuration(Number.isFinite(el.duration) ? Math.round(el.duration) : null);
      el.src = objUrl;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let data;
      if (isFile) {
        if (!file) { toast.error("Choisis un fichier."); setLoading(false); return; }
        const fd = new FormData();
        fd.append("file", file); fd.append("type", type);
        if (duration != null) fd.append("duration_seconds", String(duration));
        ({ data } = await api.post(`/tasks/${task.id}/proof/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post(`/tasks/${task.id}/proof`, { type, text: type === "TEXT" ? text : null, external_url: type === "LINK" ? url : null }));
      }
      if (data.status === "VALIDATED") {
        toast.success(`Preuve validée · +${data.xp_awarded} XP`);
        if (data.ai_review?.reason) toast.success(data.ai_review.reason);
        (data.new_badges || []).forEach(() => celebrate("badge"));
        await refresh(); onDone();
      } else if (data.status === "REJECTED") {
        toast.error(data.validation_reason || "Preuve refusée. Réessaie.");
      } else {
        toast.info("Preuve reçue — en attente de vérification.");
        onDone();
      }
    } catch (err) {
      if (err.response?.status !== 402) toast.error("Échec de l'envoi de la preuve.");
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Fournis ta preuve" testid="proof-modal">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-zinc-400">Pour terminer « {task.title} », prouve que c'est fait.{req.requires_ai_review ? " Une validation IA vérifiera la cohérence." : ""}</p>
        {types.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {types.map((tp) => {
              const M = PROOF_TYPE_META[tp] || { label: tp, icon: FileText };
              return (
                <button key={tp} type="button" onClick={() => setType(tp)} data-testid={`proof-type-${tp}`}
                  className={`px-3 h-9 rounded-full text-xs font-medium border flex items-center gap-1.5 ${type === tp ? "gold-gradient text-black border-transparent" : "border-zinc-800 text-zinc-300 hover:border-amber-500/50"}`}>
                  <M.icon className="w-3.5 h-3.5" /> {M.label}
                </button>
              );
            })}
          </div>
        )}
        {type === "TEXT" && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="proof-text"
            placeholder={req.minimum_text_length ? `Minimum ${req.minimum_text_length} caractères` : "Décris ce que tu as réalisé…"}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none resize-none" />
        )}
        {type === "LINK" && (
          <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="proof-link" placeholder="https://…"
            className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none" />
        )}
        {isFile && (
          <div className="space-y-3">
            <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-900 border border-dashed border-zinc-700 cursor-pointer hover:border-amber-500" data-testid="proof-file-label">
              <Upload className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-zinc-300 truncate">{file ? file.name : `Choisir un fichier ${PROOF_TYPE_META[type]?.label?.toLowerCase() || ""}`}</span>
              <input type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="proof-file" className="hidden" />
            </label>
            {preview && type === "IMAGE" && <img src={preview} alt="aperçu" className="max-h-48 rounded-xl border border-zinc-800 object-contain" data-testid="proof-preview-image" />}
            {preview && type === "AUDIO" && <audio src={preview} controls className="w-full" data-testid="proof-preview-audio" />}
            {preview && type === "VIDEO" && <video src={preview} controls className="w-full max-h-48 rounded-xl border border-zinc-800" data-testid="proof-preview-video" />}
            {(type === "AUDIO" || type === "VIDEO") && duration != null && (
              <p className="text-xs text-zinc-500" data-testid="proof-duration">Durée : {duration}s{req.minimum_duration_seconds ? ` (minimum requis : ${req.minimum_duration_seconds}s)` : ""}</p>
            )}
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="proof-submit"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5" /> Envoyer la preuve</>}
        </button>
      </form>
    </Modal>
  );
}

function NewTaskModal({ config, projects, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", deadline: "", priority: "simple", project_id: "", status: "À faire" });
  const [proofOn, setProofOn] = useState(false);
  const [proofType, setProofType] = useState("TEXT");
  const [aiReview, setAiReview] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, project_id: form.project_id || null };
      if (proofOn) {
        payload.proof = {
          required: true,
          allowed_types: [proofType],
          requires_ai_review: proofType === "TEXT" ? aiReview : false,
        };
      }
      const { data } = await api.post("/tasks", payload);
      toast.success("Tâche créée !");
      (data.new_badges || []).forEach(() => toast.success("🏅 Nouveau badge !"));
      onCreated();
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Nouvelle tâche" testid="new-task-modal">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Titre" value={form.title} onChange={set("title")} testid="task-title" required />
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Description</label>
          <textarea value={form.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="task-description"
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none resize-none" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Date limite" type="date" value={form.deadline} onChange={set("deadline")} testid="task-deadline" />
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Priorité (XP)</label>
            <select value={form.priority} onChange={(e) => set("priority")(e.target.value)} data-testid="task-priority"
              className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none">
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label} · +{v.xp} XP</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Projet associé</label>
          <select value={form.project_id} onChange={(e) => set("project_id")(e.target.value)} data-testid="task-project"
            className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none">
            <option value="">Aucun</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <div className="rounded-xl border border-zinc-800 p-3 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="proof-toggle">
            <input type="checkbox" checked={proofOn} onChange={(e) => setProofOn(e.target.checked)} className="accent-amber-500 w-4 h-4" />
            <span className="text-sm font-medium text-zinc-200 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-amber-400" /> Exiger une preuve pour valider</span>
          </label>
          {proofOn && (
            <div className="space-y-3 pl-1">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">Type de preuve</label>
                <select value={proofType} onChange={(e) => setProofType(e.target.value)} data-testid="proof-req-type"
                  className="w-full h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none">
                  <option value="TEXT">Texte</option>
                  <option value="IMAGE">Image</option>
                  <option value="LINK">Lien (Premium)</option>
                  <option value="AUDIO">Audio (Premium)</option>
                  <option value="VIDEO">Vidéo (Premium)</option>
                </select>
              </div>
              {proofType === "TEXT" && (
                <label className="flex items-center gap-2 cursor-pointer" data-testid="proof-ai-toggle">
                  <input type="checkbox" checked={aiReview} onChange={(e) => setAiReview(e.target.checked)} className="accent-amber-500 w-4 h-4" />
                  <span className="text-xs text-zinc-400">Validation IA de cohérence (Premium)</span>
                </label>
              )}
            </div>
          )}
        </div>
        <button type="submit" disabled={loading} data-testid="task-submit"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center hover:opacity-90 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer la tâche"}
        </button>
      </form>
    </Modal>
  );
}

const PROOF_STATUS_COLORS = {
  VALIDATED: "text-emerald-400 border-emerald-500/40",
  REJECTED: "text-red-400 border-red-500/40",
  NEEDS_MANUAL_REVIEW: "text-amber-400 border-amber-500/40",
  PENDING: "text-zinc-400 border-zinc-700",
  VALIDATING: "text-zinc-400 border-zinc-700",
};

function ProofHistory({ taskId, open, onToggle }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (open) {
      setItems(null);
      api.get(`/tasks/${taskId}/proofs`).then(({ data }) => setItems(data)).catch(() => setItems([]));
    }
  }, [open, taskId]);

  return (
    <div className="mt-3 border-t border-zinc-800 pt-3">
      <button onClick={onToggle} data-testid={`proof-history-toggle-${taskId}`}
        className="text-xs text-zinc-400 hover:text-amber-400 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> {open ? "Masquer" : "Voir"} l'historique des preuves
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`proof-history-${taskId}`}>
          {items === null ? <p className="text-xs text-zinc-600">Chargement…</p>
            : items.length === 0 ? <p className="text-xs text-zinc-600">Aucune preuve envoyée pour l'instant.</p>
            : items.map((p) => (
              <div key={p.id} className="rounded-lg bg-[#0F0F13] border border-zinc-800 p-2.5 text-xs" data-testid={`proof-item-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full border ${PROOF_STATUS_COLORS[p.status] || "text-zinc-400 border-zinc-700"}`}>{p.status}</span>
                  <span className="text-zinc-500">{p.type}</span>
                  {p.ai_review?.score != null && <span className="ml-auto text-amber-400 font-mono">IA {p.ai_review.score}/100</span>}
                </div>
                {(p.ai_review?.reason || p.validation_reason) && <p className="text-zinc-500 mt-1.5">{p.ai_review?.reason || p.validation_reason}</p>}
                {p.type === "IMAGE" && p.file_url && <ProofThumb url={p.file_url} />}
                <p className="text-zinc-600 mt-1">{fmtDate(p.submitted_at)}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}


function ProofThumb({ url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = false; let obj;
    const path = url.replace(/^\/api/, "");
    api.get(path, { responseType: "blob" }).then((res) => {
      if (!revoked) { obj = URL.createObjectURL(res.data); setSrc(obj); }
    }).catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="preuve" className="mt-2 max-h-32 min-w-16 min-h-16 rounded-lg border border-zinc-800 object-contain bg-zinc-900" data-testid="proof-thumb" />;
}

